"use client";

import { useCallback, useEffect, useState } from "react";
import { Check, Sparkles, Undo2 } from "lucide-react";
import { motion } from "framer-motion";
import { MilkdownProvider } from "@milkdown/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/shadcn/dialog";
import { Button } from "@/components/shadcn/button";
import { Separator } from "@/components/shadcn/separator";
import { NoxusTooltip } from "@/components/UI/NoxusTooltip";
import { cn } from "@/lib/utils";
import { HeuristicEvaluationResponse, InputSpec } from "@/generated";
import { HeuristicsSection } from "@/components/Form/TiptapEditor/HeuristicsSection";
import { ComplexMarkdownEditor } from "./ComplexMarkdownField";
import type { EditorInput } from "./types";

interface AIComplexMarkdownDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  // Version history: last entry is the active prompt, earlier ones are kept
  // for revert. Collapsed to a single entry when a refinement is accepted or
  // the dialog closes.
  content: string[];
  onSave: (content: string[]) => void;
  inputs: EditorInput[];
  label?: string;
  placeholder?: string;
  isDisabled?: boolean;
  nodeId?: string;
  variableBindings?: Record<string, string | undefined>;
  // Heuristics state, owned by the field so the inline badge and the dialog
  // share a single evaluation.
  enableHeuristics?: boolean;
  initialHeuristicsOpen?: boolean;
  heuristicResults?: HeuristicEvaluationResponse | null;
  isLoadingHeuristics?: boolean;
  isHeuristicsOutdated?: boolean;
  isHeuristicsError?: boolean;
  heuristicsErrorMessage?: string | null;
  canAnalyseHeuristics?: boolean;
  triggerHeuristicEvaluation?: () => void;
  scheduleHeuristicReanalysis?: () => void;
  revertHeuristicAnalysis?: () => void;
  onHeuristicTextChange?: (text: string | null) => void;
  improverContext?: string;
  improverInputs?: InputSpec[];
}

export function AIComplexMarkdownDialog({
  isOpen,
  onOpenChange,
  content,
  onSave,
  inputs,
  label,
  placeholder,
  isDisabled,
  nodeId,
  variableBindings,
  enableHeuristics,
  initialHeuristicsOpen = true,
  heuristicResults,
  isLoadingHeuristics,
  isHeuristicsOutdated,
  isHeuristicsError,
  heuristicsErrorMessage,
  canAnalyseHeuristics,
  triggerHeuristicEvaluation,
  scheduleHeuristicReanalysis,
  revertHeuristicAnalysis,
  onHeuristicTextChange,
  improverContext,
  improverInputs,
}: AIComplexMarkdownDialogProps) {
  const [editedContent, setEditedContent] = useState<string[]>(content);
  const [isHeuristicsOpen, setIsHeuristicsOpen] = useState(
    initialHeuristicsOpen
  );

  // Re-seed local history whenever the dialog (re)opens.
  useEffect(() => {
    if (isOpen) {
      setEditedContent(content.length ? content : [""]);
      setIsHeuristicsOpen(initialHeuristicsOpen);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialHeuristicsOpen]);

  const usedText =
    editedContent.length > 0 ? editedContent[editedContent.length - 1] : "";

  const hasPendingRefinement = editedContent.length > 1;
  const heuristicsPanelOpen = Boolean(enableHeuristics && isHeuristicsOpen);

  // Drive the field-level heuristic hook with the text currently shown here.
  useEffect(() => {
    if (isOpen) {
      onHeuristicTextChange?.(usedText);
    }
  }, [usedText, isOpen, onHeuristicTextChange]);

  const handleEditorChange = useCallback((md: string) => {
    setEditedContent((prev) => {
      if (prev.length === 0) return [md];
      return prev.map((v, i) => (i === prev.length - 1 ? md : v));
    });
  }, []);

  const handleAppendRefinement = useCallback((md: string) => {
    setEditedContent((prev) => [...prev, md]);
  }, []);

  const handleAccept = () => {
    if (editedContent.length > 1) {
      setEditedContent([editedContent[editedContent.length - 1]]);
    }
  };

  const handleRevert = () => {
    if (editedContent.length > 1) {
      setEditedContent(editedContent.slice(0, -1));
      revertHeuristicAnalysis?.();
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      const contentToSave =
        editedContent.length > 1
          ? [editedContent[editedContent.length - 1]]
          : editedContent;
      setEditedContent(contentToSave);
      onSave(contentToSave);
      onHeuristicTextChange?.(null);
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange} modal>
      <DialogContent
        className="bg-background flex flex-col h-[85%] w-[85%] max-w-[85%]! p-0"
        stopPropagation
        onEscapeKeyDown={(e) => e.stopPropagation()}
        onInteractOutside={(e) => {
          if ((e.target as Element)?.closest("[data-sonner-toaster]")) {
            e.preventDefault();
          }
        }}
        showCloseButton={true}
        headerButtonsClassName="top-4"
      >
        <DialogHeader className="flex flex-row flex-shrink-0 items-center justify-between px-6 py-4 border-b border-border h-12.5">
          <DialogTitle>{label || "Prompt editor"}</DialogTitle>
          <div className="flex gap-4 mr-7">
            <NoxusTooltip content="Toggle prompt optimizer">
              <Button
                onClick={() => setIsHeuristicsOpen(!isHeuristicsOpen)}
                variant="ghost"
                size="iconsm"
                aria-label="Toggle prompt optimizer"
                disabled={!enableHeuristics}
                className={cn(
                  isHeuristicsOpen && enableHeuristics && "bg-accent"
                )}
              >
                <Sparkles size={16} strokeWidth={1.33} />
              </Button>
            </NoxusTooltip>
            <Separator orientation="vertical" className="h-6 my-auto" />
          </div>
        </DialogHeader>
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Left panel - editor */}
          <div className="flex flex-col flex-1 min-w-0 p-6 pt-5 bg-background2 relative">
            <MilkdownProvider key={editedContent.length}>
              <ComplexMarkdownEditor
                value={usedText}
                onChange={handleEditorChange}
                placeholder={placeholder}
                isDisabled={isDisabled}
                inputs={inputs}
                nodeId={nodeId}
                variableBindings={variableBindings}
                fillHeight
              />
            </MilkdownProvider>

            {hasPendingRefinement && (
              <div
                className="pointer-events-none absolute left-6 right-6 top-5 bottom-6 z-5 rounded-md border border-blue-400/60 bg-blue-500/5"
                aria-hidden
              />
            )}
            {hasPendingRefinement && (
              <div className="absolute bottom-9 right-9 z-10 flex items-center rounded-md border border-border bg-background shadow-sm h-fit">
                <Button
                  onClick={handleRevert}
                  className="border-none text-sm text-muted-foreground font-medium hover:text-foreground transition-colors hover:bg-transparent"
                  variant="ghost"
                >
                  <Undo2 size={16} />
                  Revert
                </Button>
                <Separator orientation="vertical" className="h-6 my-auto" />
                <Button
                  onClick={handleAccept}
                  className="border-none text-sm text-muted-foreground font-medium hover:text-foreground transition-colors hover:bg-transparent"
                  variant="ghost"
                >
                  <Check size={16} color="var(--color-emerald-600)" />
                  Accept
                </Button>
              </div>
            )}
          </div>

          {/* Right panel - heuristics / improver */}
          <motion.div
            aria-hidden={!heuristicsPanelOpen}
            data-testid="prompt-optimizer-panel"
            data-open={heuristicsPanelOpen}
            className="flex-shrink-0 overflow-hidden"
            initial={false}
            animate={{
              width: heuristicsPanelOpen ? "40%" : "0%",
              opacity: heuristicsPanelOpen ? 1 : 0,
              x: heuristicsPanelOpen ? 0 : 24,
            }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            style={{
              minWidth: heuristicsPanelOpen ? "27.5rem" : "0",
              pointerEvents: heuristicsPanelOpen ? "auto" : "none",
            }}
          >
            <div className="h-full border-l border-border flex flex-col overflow-hidden min-w-25">
              <HeuristicsSection
                text={usedText}
                onAppendRefinement={(value) =>
                  handleAppendRefinement(value.text)
                }
                variableSyntax="var_directive"
                heuristicResults={heuristicResults ?? null}
                isLoadingHeuristics={isLoadingHeuristics ?? false}
                isOutdated={isHeuristicsOutdated ?? false}
                canAnalyse={canAnalyseHeuristics ?? false}
                triggerEvaluation={triggerHeuristicEvaluation}
                scheduleReanalysis={scheduleHeuristicReanalysis}
                improverContext={improverContext}
                improverInputs={improverInputs}
                isError={isHeuristicsError}
                errorMessage={heuristicsErrorMessage}
                retryEvaluation={triggerHeuristicEvaluation}
              />
            </div>
          </motion.div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
